/* sm-husfelag-vel.js - SMHus 3.0.0, the drawing/production engine for the Husfelagspakkinn products
   (Bilastaedaskilti, Skilti fyrir sameign, Flokkunarmerki a sorptunnur).

   3.0.0 (02.10.2026): rebuilt to the owner's SECOND Claude Design handoff
   (claude-design-v2/design_handoff_skilti/: README.md, signs.json, skilti-og-takn-v2.dc.html). The
   handoff is a CSS flex prototype at 2 px per mm; this engine reproduces its box model in mm:
     - three layouts: A "standard" (190x90, 290x90; text column left, icon 52 mm at the right edge),
       B "stacked" (the tow-truck sign on the horizontal formats: title + apartment tag on one row,
       warning + English + wide tow truck on the next), C "square" (140x140 and the new 190x140:
       icon on top, centred text). 290x190 (new, parking) is layout A/B scaled by 1.5;
     - type sizes, line heights, gaps and paddings are the handoff's px values halved (mm), text is
       measured with the real advances and kerning of TeX Gyre Heros (sm-husfelag-letur-mal.json),
       line boxes as the browser lays them out (hhea ascent/descent, line-height 1.1 / normal / 1.25);
     - fit rule (owner 02.10): a line that does not fit shrinks a little (to 90 %), then wraps into two
       balanced lines (text-wrap: balance), then shrinks further, never under a 5 mm cap height and
       never overflows: a status code blocks the cart instead;
     - 8 colour schemes on every sign (handoff bg/fg/border for the preview, supplier two-tone stock
       codes for production), the handoff's own 4 per sign listed first;
     - pictograms are the handoff's own ISO files (out/assets/sm-husfelag-takn.json, built by
       scripts/build-takn.mjs), drawn "contain" in their box like the prototype's inline SVG, the
       licensed tow-truck vector instead of the raster;
     - no engraved frame: the handoff's 3 px border is the plate edge on screen only. Holes: 2, O 4.5,
       4.5 mm in from the side edges, on the height centre line. Corner radius 6 (12 px);
     - FENUR bin labels unchanged from 2.0 (Barlow Semi Condensed 700, official plates).
   3.1.0 (01.10 owner review): 90x90 on both sign products (layout C at 0.7, no English line), washing
   machine pictogram (Tabler wash-machine, filled), sample address "Heimilisfang".
   3.2.0 (08.10 owner answers A): layout rule T1 on every format (the V1 rule of the safety signs, yfirferd/husfelag-utlit/
   TILLAGA.md): icon and text are ONE block centred on INK bounds, never on viewBox or line boxes; A (landscape): text column
   + gap 8k + icon 52k centred as a group, icon stays on the right; B (tow truck): truck 5 mm from the text column; C (square):
   icon ink top, gap 4k, text cap top; F (bin labels): gap 3.5k. The address / apartment line is TeX Gyre Heros 400 (T2's
   lighter line). The preview draws real holes: the plate is a path with the holes cut out, so the wall shows through.
   3.4.0 (08.10): plugs ON (TAPPAR_VIRKT = true), the laundry pictogram is the owner's choice A (see build-takn.mjs / takn-thvottavel.mjs).
   3.3.0 (08.10, owner review of the first 3.2.0 deploy): holes 9 mm from the edge (the door sign engine's two-tone value,
   sm-herbergi-vel.js `e`), text never under a hole; the wide formats (290 wide) fill the plate: type grows (measured, the
   longest line must still fit between the holes with margin) and the gap text -> icon is 10k there; colour scheme 991 (silver)
   added (the product has 9 colours); plugs (black / silver, hole O 8, cap O 12, the look of the door signs) are built but OFF
   behind TAPPAR_VIRKT until the price is decided.
   Thin, independent engine. 1 unit = 1 mm. ES5. No DOM access at module scope.

   PUBLIC API: STAERDIR, LITIR, FESTING, BIL_SNID, SAM_SNID, FENUR_FLOKKAR, LETUR tables, uppsetning(val),
   teikna(val) -> svg string, stada(val) -> [codes], smfram(val) -> SMFram spec, hreidra(val, merki),
   setTakn/setFenur/setLeturMal(data), skipulag(val), finnaHaed(), textBreidd() for tests. */
(function (root) {
  'use strict';
  function esc(t) { return String(t == null ? '' : t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function r3(n) { return Math.round(n * 1000) / 1000; }

  /* ---------------- 1. Fixed data ---------------- */
  /* Formats. snid: layout family. k: scale of every handoff mm value (290x190 = 1.5 x the 290x90 system).
     titill: title size in mm (handoff: 25 px on A, 23 px on 140x140; the roomier 190x140 keeps 25 px). */
  var STAERDIR = {
    '190x90': { b: 190, h: 90, snid: 'A', k: 1, titill: 12.5 },
    '290x90': { b: 290, h: 90, snid: 'A', k: 1, titill: 12.5 },
    '140x140': { b: 140, h: 140, snid: 'C', k: 1, titill: 11.5 },
    '190x140': { b: 190, h: 140, snid: 'C', k: 1, titill: 12.5 },
    '290x190': { b: 290, h: 190, snid: 'A', k: 1.5, titill: 12.5 },
    /* 90x90 (owner 01.10, small square): layout C with padding/icon/gaps at 0.7, title 9 mm, the
       address line 6 mm (4.4 mm cap, the same as the handoff's English line), no English line (it
       would fall under the cap floor). Bin labels use their own FENUR plan on this box. */
    '90x90': { b: 90, h: 90, snid: 'C', k: 0.7, titill: 9, fast: true, lina: 6, ensku: false },
    /* 90x140 (owner 08.10): the ONE bin label size, portrait. Only the FENUR plan uses it (skipulagFenur). */
    '90x140': { b: 90, h: 140, snid: 'C', k: 1, titill: 12.5, fast: true }
  };
  var STAERDIR_VARA = {
    'bilastaedaskilti': ['190x90', '290x90', '140x140', '190x140', '290x190', '90x90'],
    'skilti-fyrir-sameign': ['190x90', '290x90', '140x140', '90x90'],
    'flokkunarmerki': ['90x140']
  };

  /* Colour schemes: handoff order and labels, preview colours (bg = face, fg = engraved text and icon,
     rond = plate edge on screen), id = supplier two-tone stock code (face/core). 991 silver was added
     in 3.3.0 (the product sells 9 colours). */
  var LITIR = [
    { id: '902', lykill: 'hvitt', nafn: 'Hvítt í svart', bg: '#f6f6f3', fg: '#141414', rond: '#3a3a3a' },
    { id: '901', lykill: 'svart', nafn: 'Svart í hvítt', bg: '#141414', fg: '#ffffff', rond: '#141414' },
    { id: '904', lykill: 'blatt', nafn: 'Blátt í hvítt', bg: '#1b5c8c', fg: '#ffffff', rond: '#1b5c8c' },
    { id: '905', lykill: 'graent', nafn: 'Grænt í hvítt', bg: '#1f8a3b', fg: '#ffffff', rond: '#1f8a3b' },
    { id: '990', lykill: 'gyllt', nafn: 'Gyllt í svart', bg: '#b89a5a', fg: '#141414', rond: '#a3874a' },
    { id: '903', lykill: 'rautt', nafn: 'Rautt í hvítt', bg: '#c1202e', fg: '#ffffff', rond: '#c1202e' },
    { id: '906', lykill: 'gult', nafn: 'Gult í svart', bg: '#f1c40f', fg: '#141414', rond: '#d4ac0d' },
    { id: '917', lykill: 'dokkblatt', nafn: 'Hvítt í dökkbláu', bg: '#ffffff', fg: '#163b63', rond: '#163b63', alias: ['Hvítt í dökkblátt'] },
    /* 3.3.0: the product has 9 colours (Shopify "Silfur í svart"); the silver face of the two-tone stock (991) */
    { id: '991', lykill: 'silfur', nafn: 'Silfur í svart', bg: '#bcc6c1', fg: '#141414', rond: '#8f9994' }
  ];
  function litur(id) { for (var i = 0; i < LITIR.length; i++) if (LITIR[i].id === id || LITIR[i].lykill === id) return LITIR[i]; return LITIR[0]; }

  /* Mounts: got = 2 holes O 4.5 on the height centre line, hole edge 4.5 mm (9 px) in from the side
     edges (3.3.0: GAT_INN); got4 = 4 corner holes the same distance in; lim = tape on the back, nothing cut. */
  var FESTING = { 'got': 'Göt (2)', 'got4': 'Göt (4)', 'lim': 'Límrönd á bakhlið' };
  var GAT_R = 2.25, GAT_INN = 9;           /* hole centre 9 mm from the edge: sm-herbergi-vel.js `e` for two-tone plates */
  var TAPPI_GAT_R = 4, HETTA_R = 6, HETTA_BIL = 1; /* plug hole O 8, cap O 12, min air between cap and ink (sm-herbergi-vel.js) */
  /* Plugs (black / silver): ON since 08.10 (3.4.0); the flag stays so they can be hidden again with SMHus.setTapparVirkt(false). */
  var TAPPAR_VIRKT = true;   /* 08.10: owner decided the price (600 kr per plug, +1.200 on 2 holes, +2.400 on 4), see sm-husfelag.js aukalinur */
  var TAPPAR = { engir: 'Engir', svartir: 'Svartir tappar', silfur: 'Silfur tappar' };
  var HORN_R = 6, ROND = 1.5;

  /* Sign types (signs.json). addr/tag: {ibud} and {heimilisfang} are customer fields. litir: the
     handoff's four schemes, shown first; every scheme is allowed on every sign (owner 02.10).
     Titles with a dash are two lines (owner rule: no en dash in customer text). */
  var BIL_SNID = {
    'einkabilastaedi': { heiti: 'Einkabílastæði', addr: '{ibud}', en: 'Private parking', takn: 'tf-014-bilastaedi', litir: ['hvitt', 'svart', 'blatt', 'gyllt'] },
    'gestastaedi': { heiti: 'Gestastæði', addr: '{ibud}', en: 'Visitor parking', takn: 'tf-014-bilastaedi', litir: ['hvitt', 'svart', 'blatt', 'graent'] },
    'hledslustaedi': { heiti: 'Hleðslustæði', addr: '{heimilisfang}', en: 'EV charging only', takn: 'tf-044-hledslustod-rafbill', litir: ['hvitt', 'svart', 'blatt', 'graent'] },
    'ibuar': { heiti: 'Aðeins fyrir íbúa', addr: '{heimilisfang}', en: 'Residents only', takn: 'hus', litir: ['hvitt', 'svart', 'blatt', 'dokkblatt'] },
    'hreyfihamladir': { heiti: 'Stæði fyrir hreyfihamlaða', addr: '{heimilisfang}', en: 'Accessible parking', takn: 'pf-006-hreyfihamladir', litir: ['hvitt', 'svart', 'blatt', 'dokkblatt'] },
    'bannad-ad-leggja': { heiti: 'Bannað að leggja', addr: '{heimilisfang}', en: 'No parking', takn: 'bann-bilastaedi', litir: ['hvitt', 'svart', 'rautt', 'blatt'] },
    'ovidkomandi-fjarlaegd': { heiti: 'Einkabílastæði', tag: '{ibud}', addr: 'Óviðkomandi ökutæki fjarlægð', en: 'Unauthorised vehicles will be towed', takn: 'drattarbill', stafla: true, litir: ['hvitt', 'svart', 'rautt', 'blatt'], ekki: ['90x90'] },
    'umferd-bonnud': { heiti: 'Umferð bönnuð', addr: '{heimilisfang}', en: 'No vehicles', takn: 'bann-bill', litir: ['hvitt', 'svart', 'blatt', 'dokkblatt'] },
    'utkeyrsla': { heiti: 'Útkeyrsla - Ekki leggja', addr: '{heimilisfang}', en: 'Exit - Do not block', takn: 'pf-030-or', litir: ['hvitt', 'svart', 'gult', 'blatt'] },
    'afhending': { heiti: 'Afhending - Sendibílar', addr: '{heimilisfang}', en: 'Deliveries only', takn: 'tf-014-bilastaedi', litir: ['hvitt', 'svart', 'blatt', 'gult'] },
    /* customer text, no pictogram (kept from 2.0: the logo fee flow in sm-husfelag.js depends on it) */
    'eigin': { heiti: null, addr: null, en: null, takn: null, litir: ['hvitt', 'svart', 'blatt', 'graent'] }
  };
  var SAM_SNID = {
    'sorpgeymsla': { heiti: 'Sorpgeymsla', addr: '{heimilisfang}', en: 'Waste room', takn: 'pf-027-sorp', litir: ['hvitt', 'svart', 'blatt', 'dokkblatt'] },
    'endurvinnsla': { heiti: 'Endurvinnsla', addr: '{heimilisfang}', en: 'Recycling', takn: 'pf-066-endurvinnsla', litir: ['hvitt', 'svart', 'graent', 'blatt'] },
    'hjolageymsla': { heiti: 'Hjólageymsla', addr: '{heimilisfang}', en: 'Bicycle storage', takn: 'tf-021-hjolageymsla', litir: ['hvitt', 'svart', 'blatt', 'graent'] },
    'thvottahus': { heiti: 'Þvottahús', addr: '{heimilisfang}', en: 'Laundry room', takn: 'thvottavel', litir: ['hvitt', 'svart', 'blatt', 'gyllt'] },
    'myndavelaeftirlit': { heiti: 'Myndavélaeftirlit', addr: '{heimilisfang}', en: 'CCTV in operation', takn: 'bp-008-myndavelaeftirlit', litir: ['hvitt', 'svart', 'blatt', 'gult'] },
    'reykingar-bannadar': { heiti: 'Reykingar bannaðar', addr: '{heimilisfang}', en: 'No smoking', takn: 'p002-reykingar-bannadar', litir: ['hvitt', 'svart', 'blatt', 'dokkblatt'] },
    'hundar-bannadir': { heiti: 'Hundar bannaðir', addr: '{heimilisfang}', en: 'No dogs', takn: 'p021-hundar-bannadir', litir: ['hvitt', 'svart', 'blatt', 'dokkblatt'] }
  };
  /* sample texts (owner 01.10: the word "Heimilisfang" instead of a real street) */
  /* sizes a sign is sold in: the product's sizes minus the sign's own `ekki` list (the tow-truck sign
     does not fit 90x90: four lines plus the wide pictogram) */
  function leyfdarStaerdir(vara, snid) {
    var T = vara === 'bilastaedaskilti' ? BIL_SNID : (vara === 'skilti-fyrir-sameign' ? SAM_SNID : null);
    var all = STAERDIR_VARA[vara] || STAERDIR_VARA.bilastaedaskilti, e = T && T[snid] && T[snid].ekki;
    return e ? all.filter(function (k) { return e.indexOf(k) < 0; }) : all.slice();
  }
  var SJALFGEFID = { ibud: 'Íbúð 101', heimilisfang: 'Heimilisfang' };
  var FENUR_FLOKKAR = ['MATARLEIFAR', 'PLASTUMBÚÐIR', 'PAPPÍR', 'BLANDAÐUR ÚRGANGUR', 'GLER', 'MÁLMAR'];
  var ASCII_FLOKKAR = { 'MATARLEIFAR': 'MATARLEIFAR', 'PLASTUMBÚÐIR': 'PLASTUMBUDIR', 'PAPPÍR': 'PAPPIR', 'BLANDAÐUR ÚRGANGUR': 'BLANDADUR_URGANGUR', 'GLER': 'GLER', 'MÁLMAR': 'MALMAR' };
  /* English and Polish under the Icelandic heading (owner 08.10: always three languages). EINN STAÐUR
     fyrir þýðingarnar: breyta hér og hvergi annars staðar. OPINBER heiti FENÚR (úr _cmyk_en_pl.pdf hvers
     merkis, urgangur.is; husfelag/greining/fenur-opinber-heiti.md), stafrétt og í hástöfum eins og
     á merkjunum. PAPPI er ekki selt (enginn opinber merki-vektor, eigandi 08.10). */
  var FENUR_HEITI = {
    'MATARLEIFAR': { en: 'FOOD WASTE', pl: 'ODPADY ŻYWNOŚCIOWE' },
    'PLASTUMBÚÐIR': { en: 'PLASTIC PACKAGING', pl: 'OPAKOWANIA PLASTIKOWE' },
    'PAPPÍR': { en: 'PAPER', pl: 'PAPIER' },
    'BLANDAÐUR ÚRGANGUR': { en: 'RESIDUAL WASTE', pl: 'ODPADY ZMIESZANE' },
    'GLER': { en: 'GLASS', pl: 'SZKŁO' },
    'MÁLMAR': { en: 'METAL', pl: 'METAL' }
  };
  /* "Pakki, öll 6 merkin" (owner 08.10, 24.990 kr): the six household labels, in this order, 3 x 2 in the
     preview and in ONE production file (hopur merki-1..6). EINN STAÐUR. All six have the official FENÚR
     vector in sm-fenur.json (GLER and MÁLMAR from GLER_cmyk_icon.pdf / MÁLMAR_cmyk_icon.pdf, 08.10). */
  var FENUR_PAKKI = ['MATARLEIFAR', 'PLASTUMBÚÐIR', 'PAPPÍR', 'BLANDAÐUR ÚRGANGUR', 'GLER', 'MÁLMAR'];
  /* "Litur eftir flokki" (owner DECIDED 08.10): the two-tone stock of each category. Matarleifar 905 (green,
     white core), Pappír 904 (blue), Blandaður úrgangur 901 (black), Málmar 991 (brushed silver, black core),
     Plastumbúðir and Gler 902 (white, black core). `hex` = the official FENUR print colour (handbook
     "Samræmdar merkingar" 3. útg. 2023 p. 6, husfelag/greining/fenur.md), reference only. This is the ONLY look
     of a bin label (owner 08.10: one colour per sign; the white-on-black / black-on-white looks are gone). EINN STAÐUR. */
  var FENUR_LITIR = {
    'MATARLEIFAR': { efni: '905', hex: '#00A04B' },
    'PLASTUMBÚÐIR': { efni: '902', hex: '#961E82' },
    'PAPPÍR': { efni: '904', hex: '#0082BE' },
    'BLANDAÐUR ÚRGANGUR': { efni: '901', hex: '#141414' },
    'GLER': { efni: '902', hex: '#1DAF8E' },
    'MÁLMAR': { efni: '991', hex: '#5A6E78' }
  };

  /* Fonts. Signs: TeX Gyre Heros (GUST Font License, the free Helvetica clone the owner chose on
     02.10 for the handoff's Helvetica Neue): 700 for the title (CSS 600 falls to bold), 400 for the
     address / apartment line (3.2.0, owner 08.10) and the English line (CSS 500 falls to regular). Bin labels keep Barlow Semi Condensed
     700 (2.0 look). Ids = SMFram letur ids = out/assets/sm-letur-<id>.json. */
  var LETUR = {
    'texgyreheros': { heading: 'texgyreheros-700', lines: 'texgyreheros-700', lina: 'texgyreheros-400', en: 'texgyreheros-400', css: '"TeX Gyre Heros"' },
    'barlowsemicondensed': { heading: 'barlowsemicondensed-700', lines: 'barlowsemicondensed-700', lina: 'barlowsemicondensed-700', en: 'barlowsemicondensed-700', css: '"Barlow Semi Condensed"' }
  };
  var LETUR_SKILTI = 'texgyreheros', LETUR_TUNNUR = 'barlowsemicondensed';
  /* fallback metrics until sm-husfelag-letur-mal.json is loaded (the real hhea/cap values) */
  var MAL_SJALFGEFID = {
    'texgyreheros-700': { cap: 0.729, asc: 1.125, desc: -0.307, lineGap: 0, toppur: 0.921, botn: -0.219, medal: 0.60 },
    'texgyreheros-400': { cap: 0.729, asc: 1.148, desc: -0.284, lineGap: 0, toppur: 0.916, botn: -0.218, medal: 0.55 },
    'barlowsemicondensed-700': { cap: 0.700, asc: 1.000, desc: -0.200, lineGap: 0, toppur: 0.919, botn: -0.205, medal: 0.52 }
  };
  /* cap-height floor. Owner 02.10: "about 5 mm"; the handoff's own English line is 4.4 to 4.7 mm cap
     (12 to 13 px in Helvetica), so the floor sits just under it: nothing shrinks past 4.3 mm. */
  var GOLF_CAP_MM = 4.3;
  var MINNKA_FYRST = 0.9;       /* a line shrinks to 90 % before it wraps */

  var TAKN = {}, FENUR = {}, MAL = {};
  function setTakn(data) { TAKN = data || {}; }
  function setFenur(data) { FENUR = data || {}; }
  function setLeturMal(data) { MAL = data || {}; }

  /* ---------------- 2. Text measurement ---------------- */
  function malFyrir(leturId) {
    var m = MAL[leturId];
    if (m) return m;
    var d = MAL_SJALFGEFID[leturId] || MAL_SJALFGEFID['texgyreheros-700'];
    return { cap: d.cap, asc: d.asc, desc: d.desc, lineGap: d.lineGap, toppur: d.toppur, botn: d.botn, adv: null, kern: null, medal: d.medal };
  }
  /* width in mm of `t` in font `leturId` at size `fs` mm, with kerning (+0.5 % safety) */
  function textBreidd(t, leturId, fs) {
    t = String(t || '');
    var m = malFyrir(leturId), i, w = 0;
    if (!m.adv) return t.length * fs * (m.medal || 0.58);
    for (i = 0; i < t.length; i++) {
      var c = t.charAt(i), a = m.adv[c];
      if (a == null) a = m.adv['n'] || 0.55;
      w += a;
      if (i + 1 < t.length && m.kern) w += m.kern[c + t.charAt(i + 1)] || 0;
    }
    return w * fs * 1.005;
  }
  /* CSS line box: height of one line and the baseline offset from the line top */
  function linuBox(leturId, fs, lh) {
    var m = malFyrir(leturId), a = m.asc * fs, d = -m.desc * fs, g = (m.lineGap || 0) * fs;
    var H = lh ? lh * fs : a + d + g;
    return { h: r3(H), bl: r3((H - (a + d)) / 2 + a) };
  }

  /* ---------------- 3. Fit rule ---------------- */
  function golf(leturId) { return r3(GOLF_CAP_MM / (malFyrir(leturId).cap || 0.72)); }
  function ord(t) { return String(t).split(' ').filter(Boolean); }
  /* two lines: balanced (smallest maximum line width) or greedy (first line as full as it gets) */
  function brjota(t, leturId, fs, W, balance) {
    var w = ord(t); if (w.length < 2) return null;
    var best = null, i;
    for (i = 1; i < w.length; i++) {
      var a = w.slice(0, i).join(' '), b = w.slice(i).join(' ');
      var wa = textBreidd(a, leturId, fs), wb = textBreidd(b, leturId, fs);
      if (wa > W || wb > W) { if (balance) continue; if (wa > W) break; continue; }
      var score = balance ? Math.max(wa, wb) : -i;
      if (!best || score < best.score) best = { linur: [a, b], score: score };
    }
    return best ? best.linur : null;
  }
  /* Returns {fs, linur, passar}. Order (owner 02.10): one line at full size; one line shrunk down to
     90 %; two lines at full size (balanced or greedy); two lines shrinking in 0.1 mm steps to the
     5 mm cap floor; otherwise passar = false. A title written "A - B" is always two lines "A" / "B". */
  function finnaHaed(texti, W, fs, leturId, opts) {
    opts = opts || {};
    texti = String(texti || '').trim();
    var g = golf(leturId), h, two, i;
    if (fs < g) fs = g; /* a size under the cap floor starts at the floor */
    var fits = function (t, s) { return textBreidd(t, leturId, s) <= W + 1e-6; };
    if (!texti) return { fs: fs, linur: [''], passar: true };
    if (opts.erHeiti && (i = texti.indexOf(' - ')) > 0) {
      var b2 = texti.slice(i + 3), parts = [texti.slice(0, i), b2.charAt(0).toUpperCase() + b2.slice(1)];
      for (h = fs; h >= g - 1e-9; h = r3(h - 0.1)) if (fits(parts[0], h) && fits(parts[1], h)) return { fs: h, linur: parts, passar: true };
      return { fs: g, linur: parts, passar: false };
    }
    if (fits(texti, fs)) return { fs: fs, linur: [texti], passar: true };
    if (!opts.einLina) {
      for (h = fs; h >= Math.max(g, fs * MINNKA_FYRST) - 1e-9; h = r3(h - 0.1)) if (fits(texti, h)) return { fs: h, linur: [texti], passar: true };
      for (h = fs; h >= g - 1e-9; h = r3(h - 0.1)) {
        two = brjota(texti, leturId, h, W, !!opts.balance);
        if (two) return { fs: h, linur: two, passar: true };
      }
    }
    for (h = fs; h >= g - 1e-9; h = r3(h - 0.1)) if (fits(texti, h)) return { fs: h, linur: [texti], passar: true };
    return { fs: g, linur: [texti], passar: false };
  }

  /* ---------------- 4. uppsetning ---------------- */
  function uppsetning(val) {
    val = val || {};
    var vara = val.vara || 'bilastaedaskilti';
    var snid = val.snid || (vara === 'skilti-fyrir-sameign' ? 'sorpgeymsla' : (vara === 'flokkunarmerki' ? null : 'einkabilastaedi'));
    var sizes = leyfdarStaerdir(vara, snid);
    var lit = litur(val.litur || 'hvitt');
    var fest = FESTING[val.festing] ? val.festing : (vara === 'flokkunarmerki' ? 'lim' : 'got');
    return {
      vara: vara,
      staerd: sizes.indexOf(val.staerd) >= 0 ? val.staerd : sizes[0],
      litur: lit.id,
      snid: snid,
      linur: Array.isArray(val.linur) ? val.linur.slice(0, 2) : [],
      logo: !!val.logo,
      festing: fest,
      tappar: (TAPPAR_VIRKT && vara !== 'flokkunarmerki' && TAPPAR[val.tappar] && val.tappar !== 'engir' && (fest === 'got' || fest === 'got4')) ? val.tappar : 'engir',
      flokkur: FENUR_FLOKKAR.indexOf(val.flokkur) >= 0 ? val.flokkur : FENUR_FLOKKAR[0],
      /* bin labels: always the category colour (FENUR_LITIR); 'litur' only for the generic engine tests */
      utlit: vara === 'flokkunarmerki' ? (val.utlit === 'litur' ? 'litur' : 'flokkalitur') : (val.utlit === 'litur' ? 'litur' : 'svart'),
      pakki: vara === 'flokkunarmerki' && !!val.pakki,
      /* FB 21.10 (D1, aðallota): an empty optional field is no line on the sign; the example is only the field's placeholder */
      gata: String(val.gata == null ? '' : val.gata).trim(),
      ibud: String(val.ibud == null ? '' : val.ibud).trim(),
      ensku: val.ensku !== false,
      takn: val.takn === 'thett' ? 'thett' : 'jadar',
      magnListi: val.magnListi || null
    };
  }
  function box(v) { return STAERDIR[v.staerd] || STAERDIR['190x90']; }
  function litirFyrir(v) {
    if (v.vara === 'flokkunarmerki' && v.utlit !== 'litur') {
      var fl = litur(fenurEfni(v)); return { bg: fl.bg, fg: fl.fg, rond: fl.rond };
    }
    var c = litur(v.litur); return { bg: c.bg, fg: c.fg, rond: c.rond };
  }
  function fylla(t, v) { if (t == null) return null; var r = String(t).replace('{ibud}', v.ibud).replace('{heimilisfang}', v.gata).trim(); return r || null; }
  /* {heiti, tag, addr, en, takn, stafla} with the placeholders filled */
  function efni(v) {
    var s;
    if (v.vara === 'bilastaedaskilti') {
      s = BIL_SNID[v.snid] || BIL_SNID.einkabilastaedi;
      if (v.snid === 'eigin') return { heiti: String(v.linur[0] || '').trim(), tag: null, addr: String(v.linur[1] || '').trim() || null, en: null, takn: null, stafla: false, eigin: true };
    } else if (v.vara === 'skilti-fyrir-sameign') s = SAM_SNID[v.snid] || SAM_SNID.sorpgeymsla;
    else {
      var th = FENUR_HEITI[v.flokkur] || {};
      return { heiti: v.flokkur, tag: null, addr: null, en: th.en || null, pl: th.pl || null, takn: 'fenur-' + (ASCII_FLOKKAR[v.flokkur] || ''), stafla: false };
    }
    var bx = box(v);
    return { heiti: fylla(s.heiti, v), tag: fylla(s.tag, v), addr: fylla(s.addr, v), en: (v.ensku && bx.ensku !== false) ? s.en : null, takn: s.takn, stafla: !!s.stafla, litir: s.litir };
  }
  function taknGogn(id) {
    if (!id) return null;
    var t, p;
    if (id.indexOf('fenur-') === 0) {
      t = FENUR[id.slice(6)]; if (!t) return null;
      p = String(t.vb).split(/\s+/).map(Number);
      return { vb: t.vb, vbW: p[2], vbH: p[3], ink: [0, 0, p[2], p[3]], slodir: t.paths.map(function (d) { return { d: d }; }) };
    }
    t = TAKN[id]; if (!t || !t.slodir) return null;
    p = String(t.vb).split(/\s+/).map(Number);
    return { vb: t.vb, vbW: p[2], vbH: p[3], ink: t.ink || [0, 0, p[2], p[3]], slodir: t.slodir };
  }
  /* "object-fit: contain" placement of an icon in a box, plus its INK bounds in mm (ink = [x0, y0, x1, y1] in viewBox units) */
  function taknIBox(ikon, x, y, bw, bh) {
    var sc = Math.min(bw / ikon.vbW, bh / ikon.vbH), ink = ikon.ink || [0, 0, ikon.vbW, ikon.vbH];
    var b = { x: r3(x + (bw - ikon.vbW * sc) / 2), y: r3(y + (bh - ikon.vbH * sc) / 2), w: r3(ikon.vbW * sc), h: r3(ikon.vbH * sc), sc: sc };
    b.inkX0 = r3(b.x + ink[0] * sc); b.inkY0 = r3(b.y + ink[1] * sc); b.inkX1 = r3(b.x + ink[2] * sc); b.inkY1 = r3(b.y + ink[3] * sc);
    return b;
  }

  /* ---------------- 5. skipulag: the layout plan (mm) ---------------- */
  /* Text items: {t, fs, letur, weight, linur, baselines, x, jofnun, passar, h, w}. The content box is
     the plate minus the 1.5 mm edge (the handoff's 3 px border) minus the layout padding. */
  function textItem(t, W, fs, leturId, weight, lh, opts) {
    var fit = finnaHaed(t, W, fs, leturId, opts || {});
    var lb = linuBox(leturId, fit.fs, lh);
    var w = 0; fit.linur.forEach(function (l) { w = Math.max(w, textBreidd(l, leturId, fit.fs)); });
    return { t: t, fs: fit.fs, fs0: fs, letur: leturId, weight: weight, linur: fit.linur, passar: fit.passar, lb: lb, h: r3(lb.h * fit.linur.length), w: r3(w), lh: lh, heiti: !!(opts && opts.erHeiti) };
  }
  function placeLines(it, top) { it.baselines = it.linur.map(function (l, j) { return r3(top + j * it.lb.h + it.lb.bl); }); it.top = r3(top); }


  /* ---------------- 5b. T1 re-layout (3.2.0): everything centred on INK bounds ---------------- */
  function capAf(it) { return malFyrir(it.letur).cap * it.fsEff; }
  function leggAf(it) { return -malFyrir(it.letur).botn * it.fsEff; }
  function hefurLegg(t) { return /[gjpqyýþ]/.test(t); }
  /* a text column laid from top = 0 with gap `gap` (+ each item's mt): ink bounds from the cap top of the first line to the
     baseline (or descender) of the last one */
  function dalkur(items, gap) {
    var top = 0, f, l, lastLine, inkTop, inkBot, w = 0;
    items.forEach(function (it, i) {
      top = r3(top + (i ? gap : 0) + (it.mt || 0)); it.top = top;
      it.baselines = it.linur.map(function (ln, j) { return r3(top + j * it.lb.h + it.lb.bl); });
      top = r3(top + it.h); w = Math.max(w, it.w);
    });
    f = items[0]; l = items[items.length - 1]; lastLine = l.linur[l.linur.length - 1];
    inkTop = r3(f.baselines[0] - capAf(f));
    inkBot = r3(l.baselines[l.baselines.length - 1] + (hefurLegg(lastLine) ? leggAf(l) : 0));
    return { h: top, inkTop: inkTop, inkBot: inkBot, inkH: r3(inkBot - inkTop), w: w };
  }
  function faera(items, dx, dy) { items.forEach(function (it) { it.x = r3(it.x + dx); it.baselines = it.baselines.map(function (b) { return r3(b + dy); }); it.top = r3(it.top + dy); }); }

  function skipulag(v) {
    v = uppsetning(v);
    if (v.vara === 'flokkunarmerki') return skipulagFenur(v);
    var b = box(v), w = b.b, h = b.h, k = b.k, F = LETUR[LETUR_SKILTI];
    var e = efni(v), ikon = taknGogn(e.takn), hasIcon = !!ikon;
    var stafla = e.stafla && b.snid === 'A';
    var layout = stafla ? 'B' : b.snid;
    var mm = function (px) { return r3(px / 2 * k); }; /* handoff px -> mm, scaled */
    var items, iconBox, ofHatt, cx0, cy0, cw, ch;
    /* text and icon keep clear of the holes (and of the plug caps): nothing closer to a side edge than this */
    var hullR = v.tappar !== 'engir' ? HETTA_R : GAT_R;
    var holeEdge = (v.festing === 'got' || v.festing === 'got4') ? r3(GAT_INN + hullR + HETTA_BIL) : 0;
    var wide = layout === 'A' && b.b >= 250; /* 290 wide: the block fills the plate (3.3.0) */
    /* plan(s): the whole layout with every type size multiplied by s (1 first; smaller when the block
       is too tall for the plate, never under the 5 mm cap floor) */
    function plan(s) {
    items = []; iconBox = null; ofHatt = false;
    var fsT = r3(b.titill * (b.fast ? 1 : k) * s), fsA = r3((b.lina || mm(14)) * s), fsE = r3(mm(13) * s), fsTag = r3(mm(19) * s), fsE2 = r3(mm(12) * s);
    var it, top;
    function column(list, gap, extraTop) { /* heights + gaps -> total, extraTop[i] = margin-top */
      var H = 0; list.forEach(function (x, i) { H += (i ? gap : 0) + (extraTop[i] || 0) + x.h; }); return r3(H);
    }
    if (layout === 'A') {
      var padY = mm(18), padX = mm(32), gap = mm(24), colGap = mm(5), ic = mm(104);
      cx0 = wide ? Math.max(18, holeEdge) : Math.max(ROND + padX, holeEdge); cy0 = ROND + padY; cw = r3(w - 2 * cx0); ch = r3(h - 2 * cy0);
      /* wide: the text may use everything between the holes minus the icon's INK width (the icon's box is wider than its ink) */
      var icInk = (wide && hasIcon) ? r3((ikon.ink[2] - ikon.ink[0]) * Math.min(ic / ikon.vbW, ic / ikon.vbH)) : ic;
      var W = hasIcon ? r3(cw - gap - icInk) : cw;
      if (e.heiti) items.push(textItem(e.heiti, W, fsT, F.heading, 700, 1.1, { erHeiti: true, balance: true }));
      if (e.addr) items.push(textItem(e.addr, W, fsA, F.lina, 400, 0, {}));
      if (e.en) { it = textItem(e.en, W, fsE, F.en, 400, 0, {}); it.mt = mm(2); it.en = true; items.push(it); }
      var extra = items.map(function (x) { return x.mt || 0; });
      var colH = column(items, colGap, extra);
      if (colH > ch + 0.01) ofHatt = true;
      top = r3(cy0 + (ch - colH) / 2);
      items.forEach(function (x, i) { top = r3(top + (i ? colGap : 0) + (x.mt || 0)); placeLines(x, top); top = r3(top + x.h); x.x = cx0; x.jofnun = 'vinstri'; });
      if (hasIcon) {
        var colW = 0; items.forEach(function (x) { colW = Math.max(colW, x.w); });
        var ix = v.takn === 'thett' ? r3(cx0 + (cw - (colW + gap + ic)) / 2 + colW + gap) : r3(cx0 + cw - ic);
        if (v.takn === 'thett') { var dx = r3(cx0 + (cw - (colW + gap + ic)) / 2 - cx0); items.forEach(function (x) { x.x = r3(x.x + dx); }); }
        iconBox = taknIBox(ikon, ix, r3(cy0 + (ch - ic) / 2), ic, ic);
      }
    } else if (layout === 'B') {
      var pT = mm(18), pR = mm(30), pB = mm(16), pL = mm(34), rowGap = mm(10), g1 = mm(16), g2 = mm(14), iw = mm(170), ih = mm(56), tcGap = mm(5);
      cx0 = Math.max(ROND + pL, holeEdge); cy0 = ROND + pT; cw = r3(w - cx0 - ROND - pR); ch = r3(h - ROND - pT - ROND - pB);
      var tag = e.tag ? textItem(e.tag, 1e9, fsTag, F.lines, 700, 1.1, { einLina: true }) : null;
      var tagW = tag ? tag.w : 0;
      var heiti = textItem(e.heiti, r3(cw - (tag ? g1 + tagW : 0)), fsT, F.heading, 700, 1.1, { erHeiti: true, balance: true });
      /* row 1: baseline aligned */
      var blT = heiti.lb.bl, blG = tag ? tag.lb.bl : 0, rowBl = Math.max(blT, blG);
      var row1H = r3(Math.max(heiti.h - blT, tag ? tag.h - blG : 0) + rowBl);
      /* row 2: text column + icon, vertically centred */
      var W2 = r3(cw - g2 - iw), col = [];
      col.push(textItem(e.addr, W2, fsA, F.lina, 400, 1.25, { balance: true }));
      if (e.en) { it = textItem(e.en, W2, fsE2, F.en, 400, 1.25, { balance: true }); it.en = true; col.push(it); }
      var col2H = column(col, tcGap, []), row2H = Math.max(col2H, hasIcon ? ih : 0);
      var totalH = r3(row1H + rowGap + row2H);
      if (totalH > ch + 0.01) ofHatt = true;
      top = r3(cy0 + (ch - totalH) / 2);
      placeLines(heiti, r3(top + rowBl - blT)); heiti.x = cx0; heiti.jofnun = 'vinstri'; items.push(heiti);
      if (tag) { placeLines(tag, r3(top + rowBl - blG)); tag.x = r3(cx0 + cw); tag.jofnun = 'haegri'; tag.tag = true; items.push(tag); }
      var t2 = r3(top + row1H + rowGap), ct = r3(t2 + (row2H - col2H) / 2);
      col.forEach(function (x, i) { ct = r3(ct + (i ? tcGap : 0)); placeLines(x, ct); ct = r3(ct + x.h); x.x = cx0; x.jofnun = 'vinstri'; items.push(x); });
      if (hasIcon) iconBox = taknIBox(ikon, r3(cx0 + cw - iw), r3(t2 + (row2H - ih) / 2), iw, ih);
    } else { /* C: square, icon on top, centred text */
      var pad = mm(26), gapC = mm(12), icC = mm(104), tcg = mm(4);
      var iwC = icC, ihC = icC;
      if (e.stafla) { iwC = b.b >= 190 ? mm(150) : icC; ihC = b.b >= 190 ? mm(50) : icC; } /* the wide tow truck on 190x140 gets a wider box */
      cx0 = Math.max(ROND + pad, holeEdge); cy0 = ROND + pad; cw = r3(w - 2 * cx0); ch = r3(h - 2 * cy0);
      if (e.heiti) items.push(textItem(e.heiti, cw, fsT, F.heading, 700, 1.1, { erHeiti: true, balance: true }));
      if (e.tag) { it = textItem(e.tag, cw, fsTag, F.lines, 700, 1.1, {}); it.tag = true; items.push(it); }
      if (e.addr) items.push(textItem(e.addr, cw, fsA, F.lina, 400, 0, {}));
      if (e.en) { it = textItem(e.en, cw, fsE, F.en, 400, 0, {}); it.mt = mm(2); it.en = true; items.push(it); }
      var extraC = items.map(function (x) { return x.mt || 0; });
      var textH = column(items, tcg, extraC);
      var iconH = hasIcon ? ihC : 0, blockH = r3(iconH + (hasIcon && items.length ? gapC : 0) + textH);
      if (blockH > ch + 0.01) ofHatt = true;
      top = r3(cy0 + (ch - blockH) / 2);
      if (hasIcon) { iconBox = taknIBox(ikon, r3(cx0 + (cw - iwC) / 2), top, iwC, ihC); top = r3(top + ihC + (items.length ? gapC : 0)); }
      items.forEach(function (x, i) { top = r3(top + (i ? tcg : 0) + (x.mt || 0)); placeLines(x, top); top = r3(top + x.h); x.x = r3(w / 2); x.jofnun = 'midja'; });
    }
    }
    plan(1);
    /* too tall: shrink every type size together in 2 % steps down to the floor (the smallest item at
       its 5 mm cap); of_hatt stays set only when even that does not fit */
    var scale = 1;
    if (ofHatt) {
      var minS = 1; items.forEach(function (x) { minS = Math.min(minS, golf(x.letur) / x.fs); });
      for (scale = 0.98; scale >= minS - 1e-9; scale = r3(scale - 0.02)) { plan(scale); if (!ofHatt) break; }
      if (ofHatt) { scale = r3(Math.max(minS, 0.5)); plan(scale); ofHatt = true; }
    }
    /* wide A formats: grow every type size together (2.5 % steps, up to 1.25 of the 3.2.0 size; owner 08.10 second review: 1.8 shouted) while nothing wraps, nothing shrinks and the
       block still fits between the holes; the icon keeps its size, the gap text -> icon is 12k there */
    if (wide && !ofHatt) {
      var lines0 = items.map(function (x) { return x.linur.length; }), best = 1, gW;
      for (gW = 1.025; gW <= 1.2501; gW = r3(gW + 0.025)) {
        plan(gW);
        var okW = !ofHatt && items.every(function (x, i) { return x.passar && x.linur.length === lines0[i] && x.fs >= x.fs0 - 1e-6; });
        if (!okW) break;
        best = gW;
      }
      plan(best); scale = best;
    }
    items.forEach(function (x) { x.fsEff = x.fs; if (x.fs < golf(x.letur) - 1e-6) x.passar = false; });
    /* T1: re-place the measured items (same sizes, same line breaks); the rule is in yfirferd/husfelag-utlit/TILLAGA.md */
    (function endurrada() {
      var cy = r3(cy0 + ch / 2), d, ib0;
      if (!items.length) return;
      if (layout === 'A') {
        var ic = r3(52 * k), gapA = r3((wide ? 10 : 8) * k), colGap = r3(2.5 * k), groupW, left, dyA;
        d = dalkur(items, colGap);
        ib0 = hasIcon ? taknIBox(ikon, 0, 0, ic, ic) : null;
        /* wide: centre the group on the icon's ink (equal air at both ends); narrow formats keep the box rule */
        var icW = (wide && ib0) ? r3(ib0.inkX1 - ib0.inkX0) : ic;
        groupW = hasIcon ? r3(d.w + gapA + icW) : d.w;
        left = r3(cx0 + (cw - groupW) / 2);
        items.forEach(function (x) { x.x = left; x.jofnun = 'vinstri'; });
        dyA = r3(cy - (d.inkTop + d.inkBot) / 2);
        faera(items, 0, dyA);
        if (hasIcon) {
          var ix = r3(left + d.w + gapA - (wide ? ib0.inkX0 : 0));
          iconBox = taknIBox(ikon, ix, r3(cy - (ib0.inkY0 + ib0.inkY1) / 2), ic, ic);
        }
      } else if (layout === 'B') {
        var ixB, yB, iw = r3(85 * k), ih = r3(28 * k), rowGap = r3(5 * k), tcGap = r3(2.5 * k), gapB = r3(5 * k), d2, row2H, inkTop, inkBot, t2, colTop, dyB, colMid;
        var heiti = items.filter(function (x) { return x.heiti; })[0], tag = items.filter(function (x) { return x.tag; })[0];
        var col = items.filter(function (x) { return !x.heiti && !x.tag; });
        var blT = heiti.lb.bl, blG = tag ? tag.lb.bl : 0, rowBl = Math.max(blT, blG);
        var row1H = r3(Math.max(heiti.h - blT, tag ? tag.h - blG : 0) + rowBl);
        d2 = dalkur(col, tcGap); row2H = Math.max(d2.h, hasIcon ? ih : 0);
        inkTop = r3(rowBl - capAf(heiti));
        t2 = r3(row1H + rowGap); colTop = r3(t2 + (row2H - d2.h) / 2); inkBot = r3(colTop + d2.inkBot);
        dyB = r3(cy - (inkTop + inkBot) / 2);
        heiti.baselines = heiti.linur.map(function (ln, j) { return r3(dyB + rowBl - blT + j * heiti.lb.h + heiti.lb.bl); }); heiti.x = cx0; heiti.jofnun = 'vinstri';
        if (tag) { tag.baselines = tag.linur.map(function (ln, j) { return r3(dyB + rowBl - blG + j * tag.lb.h + tag.lb.bl); }); tag.x = r3(cx0 + cw); tag.jofnun = 'haegri'; }
        col.forEach(function (x) { x.x = cx0; x.jofnun = 'vinstri'; });
        faera(col, 0, r3(dyB + colTop));
        if (hasIcon) {
          ixB = r3(cx0 + d2.w + gapB);
          ib0 = taknIBox(ikon, ixB, 0, iw, ih);
          colMid = r3(dyB + colTop + (d2.inkTop + d2.inkBot) / 2);
          yB = r3(colMid - (ib0.inkY0 + ib0.inkY1) / 2);
          iconBox = taknIBox(ikon, ixB, yB, iw, ih);
        }
        /* equal air above and below the WHOLE block (text rows and the truck): shift everything by half the difference */
        (function () {
          var top = 1e9, bot = -1e9, sh;
          items.forEach(function (x) {
            var last = x.linur[x.linur.length - 1];
            top = Math.min(top, x.baselines[0] - capAf(x)); bot = Math.max(bot, x.baselines[x.baselines.length - 1] + (hefurLegg(last) ? leggAf(x) : 0));
          });
          if (iconBox) { top = Math.min(top, iconBox.inkY0); bot = Math.max(bot, iconBox.inkY1); }
          sh = r3(((h - ROND - bot) - (top - ROND)) / 2);
          faera(items, 0, sh);
          if (iconBox) iconBox = taknIBox(ikon, ixB, r3(yB + sh), iw, ih);
        })();
      } else { /* C */
        var icC2 = r3(52 * k), gapC2 = r3(4 * k), tcg2 = r3(2 * k), iw2 = icC2, ih2 = icC2, iconInkH, H, top0, textInkTop;
        if (e.stafla) { iw2 = b.b >= 190 ? r3(75 * k) : icC2; ih2 = b.b >= 190 ? r3(25 * k) : icC2; }
        d = dalkur(items, tcg2);
        ib0 = hasIcon ? taknIBox(ikon, r3(cx0 + (cw - iw2) / 2), 0, iw2, ih2) : null;
        iconInkH = ib0 ? r3(ib0.inkY1 - ib0.inkY0) : 0;
        H = r3(iconInkH + (ib0 ? gapC2 : 0) + d.inkH);
        top0 = r3(cy - H / 2);
        if (ib0) iconBox = taknIBox(ikon, ib0.x, r3(top0 - ib0.inkY0), iw2, ih2); /* ib0 was placed at y = 0: its ink top is the offset */
        textInkTop = r3(top0 + iconInkH + (ib0 ? gapC2 : 0));
        items.forEach(function (x) { x.x = r3(w / 2); x.jofnun = 'midja'; });
        faera(items, 0, r3(textInkTop - d.inkTop));
      }
    })();
    return {
      b: b, w: w, h: h, k: k, layout: layout, efni: e, hasIcon: hasIcon, ikon: ikon, iconBox: iconBox, items: items,
      content: { x: cx0, y: cy0, w: cw, h: ch }, ofHatt: ofHatt, scale: scale, litir: e.litir || [],
      headingFit: items.length && items[0].heiti ? { haed: items[0].fs, passar: items[0].passar } : { haed: 0, passar: true }
    };
  }
  /* FENUR bin label (2.0; 3.2.0: gap 3.5k): 90 x 90 scaled by k for 140 x 140, the official artboard as one
     virtual square S = 56k, name in capitals (Barlow Semi Condensed 700) under it, block centred. */
  /* Bin label 90 x 140 portrait (owner 08.10): pictogram on top, the Icelandic FENUR heading in Barlow
     Semi Condensed 700 capitals (8 mm cap), then English and Polish in TeX Gyre Heros 400 (5 mm cap,
     sentence case; the project has no Barlow regular and nothing is downloaded), everything centred,
     generous gaps. Each item carries its own font (letur = SMFram id, fam = CSS family for the preview). */
  function skipulagFenur(v) {
    var b = box(v), w = b.b, h = b.h, k = w / 90, FB = LETUR[LETUR_TUNNUR], FH = LETUR[LETUR_SKILTI];
    var mB = malFyrir(FB.heading), mH = malFyrir(FH.lina);
    var m = r3(8 * k), S = r3(54 * k), gapIk = r3(7 * k), gapIS = r3(5 * k), gapX = r3(3.5 * k);
    var fsIS = r3(8 * k / (mB.cap || 0.7)), fsX = r3(5 * k / (mH.cap || 0.729));
    var e = efni(v), ikon = taknGogn(e.takn);
    var contentW = r3(w - 2 * m), contentH = r3(h - 2 * m);
    function lina(t, fs, leturId, cap) {
      if (!t) return null;
      var fit = finnaHaed(t, contentW, fs, leturId, { balance: true }), c = cap * fit.fs, lh = 0.3 * fit.fs;
      return { fit: fit, cap: c, lh: lh, h: r3(c + (fit.linur.length - 1) * (c + lh)) };
    }
    var L = [
      { l: lina(e.heiti, fsIS, FB.heading, mB.cap || 0.7), letur: FB.heading, fam: FB.css, weight: 700, heiti: true, gap: gapIk },
      { l: lina(e.en, fsX, FH.lina, mH.cap || 0.729), letur: FH.lina, fam: FH.css, weight: 400, heiti: false, gap: gapIS },
      { l: lina(e.pl, fsX, FH.lina, mH.cap || 0.729), letur: FH.lina, fam: FH.css, weight: 400, heiti: false, gap: gapX }
    ].filter(function (x) { return x.l; });
    var textH = 0; L.forEach(function (x) { textH = r3(textH + x.gap + x.l.h); });
    /* the ink of the last line reaches below its baseline when it has descenders (y, p, ę ...): count
       that in the block so the INK is centred, not only the baselines */
    var last = L[L.length - 1], lastLine = last ? last.l.fit.linur[last.l.fit.linur.length - 1] : '';
    var dsc = last && /[gjpqyýþ]/.test(lastLine) ? r3(Math.abs(malFyrir(last.letur).botn || 0.22) * last.l.fit.fs) : 0;
    var picH = ikon ? S : 0, blockH = r3(picH + textH + dsc);
    if (blockH > contentH) { picH = Math.max(0, r3(contentH - textH)); blockH = r3(picH + textH); }
    var top = r3(m + (contentH - blockH) / 2);
    var iconBox = ikon ? taknIBox(ikon, r3(w / 2 - picH / 2), top, picH, picH) : null;
    var y = r3(top + picH), items = [];
    L.forEach(function (x) {
      var baselines = [], f = x.l.fit;
      y = r3(y + x.gap + x.l.cap);
      f.linur.forEach(function (t, j) { if (j > 0) y = r3(y + x.l.cap + x.l.lh); baselines.push(y); });
      items.push({ t: f.linur.join(' '), fs: f.fs, fsEff: f.fs, letur: x.letur, fam: x.fam, weight: x.weight, linur: f.linur, baselines: baselines, passar: f.passar, heiti: x.heiti, x: r3(w / 2), jofnun: 'midja', ls: x.heiti ? 0.01 : 0 });
    });
    var fit0 = L[0] ? L[0].l.fit : { fs: fsIS, passar: true };
    return { b: b, w: w, h: h, k: k, layout: 'F', efni: e, hasIcon: !!ikon, ikon: ikon, iconBox: iconBox, items: items, content: { x: m, y: m, w: contentW, h: contentH }, ofHatt: false, scale: 1, fenur: true, litir: [], headingFit: { haed: fit0.fs, passar: fit0.passar } };
  }
  /* two-tone stock code of a bin label: always from FENUR_LITIR (the category colour) */
  function fenurEfni(v) {
    if (v.vara !== 'flokkunarmerki' || v.utlit === 'litur') return v.litur;
    return (FENUR_LITIR[v.flokkur] || FENUR_LITIR.MATARLEIFAR).efni;
  }
  /* the six labels of the package as separate states (same look, mount and size, one category each) */
  function pakkiMerki(v) {
    return FENUR_PAKKI.map(function (f) { var vi = {}; for (var k in v) vi[k] = v[k]; vi.flokkur = f; vi.pakki = false; return vi; });
  }
  var PAKKI_DALKAR = 3, PAKKI_BIL = 5;
  function pakkiStada(i, b) { return { dx: r3((i % PAKKI_DALKAR) * (b.b + PAKKI_BIL)), dy: r3(Math.floor(i / PAKKI_DALKAR) * (b.h + PAKKI_BIL)) }; }
  function pakkiMal(b) { var r = Math.ceil(FENUR_PAKKI.length / PAKKI_DALKAR); return { w: r3(PAKKI_DALKAR * b.b + (PAKKI_DALKAR - 1) * PAKKI_BIL), h: r3(r * b.h + (r - 1) * PAKKI_BIL) }; }

  /* ---------------- 6. geometry ---------------- */
  /* holes: [cx, cy, r]; plugs need O 8 holes, screws O 4.5 */
  function gotin(v, w, h) {
    var r = v.tappar !== 'engir' ? TAPPI_GAT_R : GAT_R;
    if (v.festing === 'got') return [[GAT_INN, r3(h / 2), r], [r3(w - GAT_INN), r3(h / 2), r]];
    if (v.festing === 'got4') return [[GAT_INN, GAT_INN, r], [r3(w - GAT_INN), GAT_INN, r], [GAT_INN, r3(h - GAT_INN), r], [r3(w - GAT_INN), r3(h - GAT_INN), r]];
    return [];
  }
  function fontCss(sk) { return (sk.fenur ? LETUR[LETUR_TUNNUR] : LETUR[LETUR_SKILTI]).css.replace(/"/g, "'"); }

  /* ---------------- 6b. plugs (copied from sm-herbergi-vel.js tappi(): the look of the door signs and house numbers) ---------------- */
  var TAPPI_LIT = {
    silfur: { lin: ["#c9c9c5", "#ababa7", "#8b8b88"], keila: [[255, 0.18], [0, 0.14], [255, 0.12], [0, 0.16], [255, 0.18]], skuggi: 0.36, hringur: 0.38, ljos: 0.5, dokk: 0.24 },
    svart: { lin: ["#484643", "#322f2d", "#1d1c1a"], keila: [[255, 0.11], [0, 0.18], [255, 0.07], [0, 0.2], [255, 0.11]], skuggi: 0.45, hringur: 0.6, ljos: 0.24, dokk: 0.35 }
  };
  function f3(n) { var v = Math.round(n * 1000) / 1000; return String(v === 0 ? 0 : v); }
  function hringD(cx, cy, r) {
    return "M" + f3(cx + r) + " " + f3(cy) + "a" + f3(r) + " " + f3(r) + " 0 1 0 " + f3(-2 * r) + " 0a" + f3(r) + " " + f3(r) + " 0 1 0 " + f3(2 * r) + " 0Z";
  }
  /* litur keilunnar m gr\u00e1\u00f0um eftir upphafinu (from 200deg): [gr\u00e1t\u00f3nn 0-255, alfa], forsamfellt (premultiplied) eins og CSS */
  function keiluLitur(K, m) {
    m = ((m % 360) + 360) % 360;
    var j = Math.min(3, Math.floor(m / 100)), s0 = K[j], s1 = K[j + 1], q = (m - j * 100) / (j === 3 ? 60 : 100);
    var al = s0[1] + (s1[1] - s0[1]) * q;
    return [Math.round((s0[0] * s0[1] + (s1[0] * s1[1] - s0[0] * s0[1]) * q) / al), al];
  }
  /* Keilustigullinn sem PNG (64 x 64), einu sinni fyrir hvorn lit. SVG fleygar m\u00e6tast \u00ed mi\u00f0junni og \u00fear
     leggst br\u00fanam\u00fdking \u00feeirra saman \u00ed d\u00f6kkan punkt; myndin er reiknu\u00f0 punkt fyrir punkt og hefur hann ekki. */
  var keiluMyndir = {};
  function keiluMynd(litur) {
    if (keiluMyndir[litur] !== undefined) return keiluMyndir[litur];
    var url = "";
    try {
      var doc = typeof document !== "undefined" ? document : null, cv = doc && doc.createElement ? doc.createElement("canvas") : null;
      var cx = cv && cv.getContext ? cv.getContext("2d") : null;
      if (cx) {
        var n = 64, K = TAPPI_LIT[litur].keila, im, x, y, i, v;
        cv.width = n; cv.height = n; im = cx.createImageData(n, n);
        for (y = 0; y < n; y++) for (x = 0; x < n; x++) {
          v = keiluLitur(K, Math.atan2(x + 0.5 - n / 2, n / 2 - y - 0.5) * 180 / Math.PI - 200);
          i = (y * n + x) * 4; im.data[i] = im.data[i + 1] = im.data[i + 2] = v[0]; im.data[i + 3] = Math.round(v[1] * 255);
        }
        cx.putImageData(im, 0, 0);
        url = cv.toDataURL("image/png");
        if (url.indexOf("data:image/png") !== 0) url = "";
      }
    } catch (e) { url = ""; }
    return (keiluMyndir[litur] = url);
  }
  function tappi(id, svartir, pxmm, sma) {
    var L = TAPPI_LIT[svartir ? "svart" : "silfur"], r = HETTA_R, u = 1 / pxmm, t = "", i;
    /* 155deg \u00e1 ferningi hettunnar (objectBoundingBox): stefna (sin 155, -cos 155), h\u00e1lf lengd (|sin|+|cos|)/2 */
    t += '<linearGradient id="' + id + 'tl" x1=".219187" y1="-.10221" x2=".780813" y2="1.10221">' +
      '<stop offset="0" stop-color="' + L.lin[0] + '"/><stop offset=".5" stop-color="' + L.lin[1] + '"/><stop offset="1" stop-color="' + L.lin[2] + '"/></linearGradient>';
    if (sma) {
      /* sm\u00e1mynd: hringur (1 skj\u00e1pixill utan vi\u00f0 hettuna) og l\u00ednustigullinn, s\u00f6mu litir */
      return { defs: t + '<g id="' + id + 'tg"><circle r="' + r + '" fill="none" stroke="#000" stroke-opacity="' + L.hringur + '" stroke-width="2" vector-effect="non-scaling-stroke"/>' +
        '<circle r="' + r + '" fill="url(#' + id + 'tl)"/></g>' };
    }
    t += '<clipPath id="' + id + 'tc"><circle r="' + r + '"/></clipPath>';
    t += '<filter id="' + id + 'tf" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="' + f3(2.5 * u) + '"/></filter>';
    t += '<filter id="' + id + 'tb" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="' + f3(u) + '"/></filter>';
    /* keilan: mynd reiknu\u00f0 punkt fyrir punkt (sama form\u00fala og CSS), annars 120 fleygar (t.d. \u00ed node) */
    var km = keiluMynd(svartir ? "svart" : "silfur"), k = "";
    if (km) k = '<image href="' + km + '" x="' + (-r) + '" y="' + (-r) + '" width="' + 2 * r + '" height="' + 2 * r + '" preserveAspectRatio="none"/>';
    else {
      var N = 120, R = r * 1.2, pt = [];
      for (i = 0; i <= N; i++) { var a = (200 + i * 360 / N) * Math.PI / 180; pt.push(f3(R * Math.sin(a)) + " " + f3(-R * Math.cos(a))); }
      for (i = 0; i < N; i++) {
        var kl = keiluLitur(L.keila, (i + 0.5) * 360 / N);
        k += '<path d="M0 0L' + pt[i] + "L" + pt[i + 1] + 'Z" fill="rgb(' + kl[0] + "," + kl[0] + "," + kl[0] + ')" fill-opacity="' + f3(kl[1]) + '"/>';
      }
    }
    var B = r + 6 * u, rammi = "M" + f3(-B) + " " + f3(-B) + "H" + f3(B) + "V" + f3(B) + "H" + f3(-B) + "Z";
    t += '<g id="' + id + 'tg">' +
      /* utan vi\u00f0 hettuna: hringurinn (0 0 0 1px) og skugginn (0 2px 5px) ofan \u00e1 honum */
      '<circle r="' + f3(r + u) + '" fill="#000" fill-opacity="' + L.hringur + '"/>' +
      '<circle cy="' + f3(2 * u) + '" r="' + r + '" fill="#000" fill-opacity="' + L.skuggi + '" filter="url(#' + id + 'tf)"/>' +
      /* hettan: l\u00ednustigull og keila */
      '<circle r="' + r + '" fill="url(#' + id + 'tl)"/>' +
      '<g clip-path="url(#' + id + 'tc)">' + k +
      /* inni \u00ed hettunni: d\u00f6kk ne\u00f0ri br\u00fan (inset 0 -2px 2px) og lj\u00f3s efri br\u00fan (inset 0 1px 0) */
      '<path d="' + rammi + hringD(0, -2 * u, r) + '" fill-rule="evenodd" fill="#000" fill-opacity="' + L.dokk + '" filter="url(#' + id + 'tb)"/>' +
      '<path d="' + rammi + hringD(0, u, r) + '" fill-rule="evenodd" fill="#fff" fill-opacity="' + L.ljos + '"/></g></g>';
    return { defs: t };
  }

  /* ---------------- 7. teikna: SVG preview ---------------- */
  /* a hole is a real cut-out: the plate is ONE path (rounded rectangle + circles, even-odd), so the wall shows through; a thin
     dark rim and the crescent of shade that the plate edge throws on the wall make it read as a drilled hole (3.2.0) */
  var tappiNr = 0;
  function plataD(w, h, r) {
    return 'M' + r + ' 0H' + r3(w - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + w + ' ' + r + 'V' + r3(h - r) + 'A' + r + ' ' + r + ' 0 0 1 ' + r3(w - r) + ' ' + h +
      'H' + r + 'A' + r + ' ' + r + ' 0 0 1 0 ' + r3(h - r) + 'V' + r + 'A' + r + ' ' + r + ' 0 0 1 ' + r + ' 0Z';
  }
  function gatD(cx, cy, r) { return 'M' + r3(cx + r) + ' ' + cy + 'a' + r + ' ' + r + ' 0 1 0 ' + r3(-2 * r) + ' 0a' + r + ' ' + r + ' 0 1 0 ' + r3(2 * r) + ' 0Z'; }
  /* top crescent of a hole: inside the circle, outside the same circle moved d down (two equal circles, intersection on y = cy + d/2) */
  function gatSkuggiD(cx, cy, r, d) {
    var xs = Math.sqrt(r * r - d * d / 4), yi = r3(cy + d / 2);
    return 'M' + r3(cx - xs) + ' ' + yi + 'A' + r + ' ' + r + ' 0 1 1 ' + r3(cx + xs) + ' ' + yi + 'A' + r + ' ' + r + ' 0 0 0 ' + r3(cx - xs) + ' ' + yi + 'Z';
  }
  function teikna(valRaw) {
    var v = uppsetning(valRaw), out = [];
    if (v.pakki) {
      /* the package: six plates 3 x 2 in one drawing, each drawn exactly like a single label */
      var b0 = box(v), pm = pakkiMal(b0);
      out.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + pm.w + ' ' + pm.h + '" width="' + pm.w + 'mm" height="' + pm.h + 'mm">');
      pakkiMerki(v).forEach(function (vi, i) {
        var p = pakkiStada(i, b0);
        out.push('<g transform="translate(' + p.dx + ',' + p.dy + ')">'); teiknaEitt(vi, out); out.push('</g>');
      });
      out.push('</svg>');
      return out.join('');
    }
    var sk0 = skipulag(v);
    out.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + sk0.w + ' ' + sk0.h + '" width="' + sk0.w + 'mm" height="' + sk0.h + 'mm">');
    teiknaEitt(v, out);
    out.push('</svg>');
    return out.join('');
  }
  /* one plate's elements at origin 0,0 (plate, edge, pictogram, text, holes), pushed onto out */
  function teiknaEitt(v, out) {
    var sk = skipulag(v), w = sk.w, h = sk.h, c = litirFyrir(v), fam = fontCss(sk), gat = gotin(v, w, h);
    var tappar = v.tappar !== 'engir' && gat.length, tdefs = '', tid = 'hft' + (++tappiNr) + '-';
    if (tappar) { tdefs = tappi(tid, v.tappar === 'svartir', 2.2, false).defs; out.push('<defs>' + tdefs + '</defs>'); }
    out.push('<path d="' + plataD(w, h, HORN_R) + gat.map(function (p) { return gatD(p[0], p[1], p[2]); }).join('') + '" fill="' + c.bg + '" fill-rule="evenodd"/>');
    /* the plate edge as the handoff draws it on screen (3 px border, 12 px radius): nothing engraved */
    if (!sk.fenur) out.push('<rect x="' + ROND / 2 + '" y="' + ROND / 2 + '" width="' + r3(w - ROND) + '" height="' + r3(h - ROND) + '" rx="' + r3(HORN_R - ROND / 2) + '" ry="' + r3(HORN_R - ROND / 2) + '" fill="none" stroke="' + c.rond + '" stroke-width="' + ROND + '"/>');
    if (sk.ikon && sk.iconBox) {
      var ib = sk.iconBox;
      out.push('<g transform="translate(' + ib.x + ',' + ib.y + ') scale(' + r3(ib.sc) + ')">');
      sk.ikon.slodir.forEach(function (s) { out.push('<path d="' + s.d + '" fill="' + (s.gat ? c.bg : c.fg) + '" fill-rule="nonzero"/>'); });
      out.push('</g>');
    }
    sk.items.forEach(function (it) {
      var anchor = it.jofnun === 'midja' ? 'middle' : (it.jofnun === 'haegri' ? 'end' : 'start');
      it.linur.forEach(function (sub, j) {
        out.push('<text x="' + it.x + '" y="' + it.baselines[j] + '" text-anchor="' + anchor + '" font-family="' + (it.fam ? it.fam.replace(/"/g, "'") : fam) + '" font-weight="' + it.weight + '" font-size="' + it.fsEff + '"' + (it.ls ? ' letter-spacing="' + r3(it.ls * it.fsEff) + '"' : '') + ' fill="' + c.fg + '">' + esc(sub) + '</text>');
      });
    });
    /* screws in the holes (preview only, the production file is unchanged): the steel head of the cross signs (sm-krossaskilti.css .kr-hole:
       radial steel gradient and a slot, alternating angle) */
    if (gat.length && !tappar && !sk.fenur) out.push('<defs><radialGradient id="' + tid + 'sk" cx=".36" cy=".3" r=".75"><stop offset="0" stop-color="#e8ebee"/><stop offset=".32" stop-color="#c4c9cf"/><stop offset=".68" stop-color="#969ca3"/><stop offset="1" stop-color="#686e75"/></radialGradient></defs>');
    gat.forEach(function (p, i) {
      if (tappar) { out.push('<use href="#' + tid + 'tg" x="' + p[0] + '" y="' + p[1] + '"/>'); return; }
      out.push('<path d="' + gatSkuggiD(p[0], p[1], p[2], 0.7) + '" fill="#000" fill-opacity="0.34"/>');
      out.push('<circle cx="' + p[0] + '" cy="' + p[1] + '" r="' + p[2] + '" fill="none" stroke="#000" stroke-opacity="0.45" stroke-width="0.3"/>');
      if (!sk.fenur) {
        var rs = r3(p[2] * 0.92), an = (i % 2 ? 58 : -58) * Math.PI / 180, ax = r3(Math.cos(an) * rs * 0.72), ay = r3(Math.sin(an) * rs * 0.72);
        out.push('<circle cx="' + p[0] + '" cy="' + p[1] + '" r="' + rs + '" fill="url(#' + tid + 'sk)" stroke="#00000059" stroke-width="0.2"/>');
        out.push('<path d="M' + r3(p[0] - ax) + ' ' + r3(p[1] - ay) + 'L' + r3(p[0] + ax) + ' ' + r3(p[1] + ay) + '" stroke="#000" stroke-opacity="0.55" stroke-width="0.4" stroke-linecap="round"/>');
      }
    });
  }

  /* ---------------- 8. stada ---------------- */
  function stada(valRaw) {
    var v = uppsetning(valRaw), codes = [], sk;
    if (v.pakki) {
      pakkiMerki(v).forEach(function (vi) { stada(vi).forEach(function (c) { if (codes.indexOf(c) < 0) codes.push(c); }); });
      return codes;
    }
    sk = skipulag(v);
    if (v.vara === 'bilastaedaskilti' && v.snid === 'eigin' && !sk.efni.heiti) codes.push('vantar_texta');
    sk.items.forEach(function (it) { if (!it.passar && codes.indexOf(it.heiti ? 'fyrirsogn_of_long' : 'lina_of_long') < 0) codes.push(it.heiti ? 'fyrirsogn_of_long' : 'lina_of_long'); });
    if (sk.ofHatt) codes.push('of_hatt');
    if (v.vara === 'bilastaedaskilti' && v.magnListi && v.magnListi.radir) {
      var seen = {};
      v.magnListi.radir.forEach(function (r) { if (seen[r]) codes.push('tvitekning'); seen[r] = true; });
    }
    return codes;
  }

  /* ---------------- 9. smfram: production spec ---------------- */
  /* GRAFA: pictogram paths and text (TeX Gyre Heros outlines via the registered sm-letur json);
     SKERA-INNRA: the holes; SKERA-YTRA: the plate outline R 6. No frame line (owner 02.10). */
  function hlutirAf(v, sk, dx, dy) {
    var w = sk.w, h = sk.h, hl = [], tr = (dx || dy) ? 'translate(' + r3(dx || 0) + ',' + r3(dy || 0) + ')' : '';
    var push = function (o) { if (!o.fylki) delete o.fylki; hl.push(o); };
    if (sk.ikon && sk.iconBox) {
      var ib = sk.iconBox, f2 = ((tr ? tr + ' ' : '') + 'translate(' + ib.x + ',' + ib.y + ') scale(' + r3(ib.sc) + ')').trim();
      sk.ikon.slodir.forEach(function (s) { push({ gerd: 'slod', lag: 'GRAFA', d: s.d, fylki: f2, gat: !!s.gat }); });
    }
    sk.items.forEach(function (it) {
      it.linur.forEach(function (sub, j) {
        if (!sub) return;
        push({ gerd: 'texti', lag: 'GRAFA', texti: sub, letur: it.letur, mm: it.fsEff, x: r3(it.x + (dx || 0)), y: r3(it.baselines[j] + (dy || 0)), jofnun: it.jofnun, stafabil: it.ls || 0 });
      });
    });
    gotin(v, w, h).forEach(function (p) { push({ gerd: 'hringur', lag: 'SKERA-INNRA', cx: r3(p[0] + (dx || 0)), cy: r3(p[1] + (dy || 0)), r: p[2] }); });
    push({ gerd: 'rett', lag: 'SKERA-YTRA', x: r3(dx || 0), y: r3(dy || 0), w: w, h: h, r: HORN_R });
    return hl;
  }
  function lysingAf(v, sk) {
    var l = [['Vara', v.vara], ['Stærð', v.staerd.replace('x', ' x ') + ' mm'], ['Litur', v.vara === 'flokkunarmerki' && v.utlit !== 'litur' ? 'Eftir flokki (' + fenurEfni(v) + ')' : litur(v.litur).nafn + ' (' + v.litur + ')'], ['Festing', FESTING[v.festing]]];
    if (v.tappar !== 'engir') l.push(['Tappar', TAPPAR[v.tappar]]);
    if (sk.efni.heiti) l.push(['Fyrirsögn', sk.efni.heiti]);
    if (sk.efni.tag) l.push(['Íbúð', sk.efni.tag]);
    if (sk.efni.addr) l.push(['Lína', sk.efni.addr]);
    if (sk.efni.en) l.push(['Enska', sk.efni.en]);
    if (sk.efni.pl) l.push(['Pólska', sk.efni.pl]);
    l.push(['Letur', fontCss(sk).replace(/'/g, '') + ', fyrirsögn ' + (sk.items[0] ? sk.items[0].fsEff : 0) + ' mm']);
    return l;
  }
  function smfram(valRaw) {
    var v = uppsetning(valRaw), sk, b;
    if (v.pakki) {
      /* ONE production file with the six labels 3 x 2 (hopur merki-1..6), each label its own stock code
         (efni on every element and in gogn.merki) so "Litur eftir flokki" can be cut from several sheets;
         the top-level efni is the code when all six share it, otherwise the first one and gogn.efni_blandad */
      b = box(v); var pm = pakkiMal(b), hl = [], merki = [], kodar = [];
      /* the labels are emitted grouped by stock code (same material = adjacent layers), and every group id
         carries its code: GRAFA-merki-1-905, SKERA-INNRA-merki-1-905 ... so the workshop can cut each material
         from its own sheet; the placement (3 x 2) is unchanged */
      var rodMerki = pakkiMerki(v).map(function (vi, i) { return { vi: vi, i: i, kodi: fenurEfni(vi) }; });
      rodMerki.sort(function (a, c) { return a.kodi < c.kodi ? -1 : (a.kodi > c.kodi ? 1 : a.i - c.i); });
      rodMerki.forEach(function (m) {
        var vi = m.vi, i = m.i, kodi = m.kodi, p = pakkiStada(i, b), ski = skipulag(vi), hop = 'merki-' + (i + 1) + '-' + kodi;
        hlutirAf(vi, ski, p.dx, p.dy).forEach(function (x) { x.hopur = hop; x.efni = kodi; hl.push(x); });
        merki.push({ hopur: hop, flokkur: vi.flokkur, efni: kodi, x: p.dx, y: p.dy, w: b.b, h: b.h, en: ski.efni.en, pl: ski.efni.pl });
      });
      merki.sort(function (a, c) { return parseInt(a.hopur.split('-')[1], 10) - parseInt(c.hopur.split('-')[1], 10); });
      merki.forEach(function (m) { if (kodar.indexOf(m.efni) < 0) kodar.push(m.efni); });
      var lys = [['Vara', 'flokkunarmerki, pakki'], ['Merki', FENUR_PAKKI.length + ' stk, 3 x 2'], ['Stærð', b.b + ' x ' + b.h + ' mm hvert'],
        ['Litur', 'Eftir flokki (' + kodar.join(', ') + ')'],
        ['Festing', FESTING[v.festing]], ['Flokkar', FENUR_PAKKI.join(', ')], ['Efni á merki', merki.map(function (m) { return m.hopur.split('-')[1] + ' ' + m.flokkur + ' ' + m.efni; }).join('; ')], ['Letur', 'Barlow Semi Condensed 700 + TeX Gyre Heros 400']];
      return {
        vara: v.vara, snid: v.vara + '-pakki@3', staerd: v.staerd, efni: kodar[0], w: pm.w, h: pm.h, breidd: pm.w, haed: pm.h, stykki: FENUR_PAKKI.length,
        hlutir: hl, lysing: lys,
        gogn: { vara: v.vara, pakki: true, staerd: v.staerd, utlit: v.utlit, litur: kodar[0], efni_blandad: kodar.length > 1 ? kodar : undefined, merki: merki, festing: v.festing }
      };
    }
    sk = skipulag(v); b = sk.b;
    return {
      vara: v.vara, snid: v.vara + '@3', staerd: v.staerd, efni: fenurEfni(v), w: b.b, h: b.h, breidd: b.b, haed: b.h,
      hlutir: hlutirAf(v, sk, 0, 0), lysing: lysingAf(v, sk),
      gogn: { vara: v.vara, staerd: v.staerd, litur: fenurEfni(v), snid: v.snid, flokkur: v.vara === 'flokkunarmerki' ? v.flokkur : undefined, gata: v.gata, ibud: v.ibud, ensku: v.ensku, festing: v.festing, tappar: v.tappar === 'engir' ? undefined : v.tappar, linur: v.linur }
    };
  }

  /* ---------------- 10. hreidra: N signs of one order on 600 x 300 sheets ---------------- */
  var SHEET_B = 600, SHEET_H = 300, BIL_MM = 5, SHEET_GAP = 20;
  function hreidra(valRaw, merki) {
    var v = uppsetning(valRaw), b = box(v);
    var perRow = Math.max(1, Math.floor((SHEET_B + BIL_MM) / (b.b + BIL_MM)));
    var perCol = Math.max(1, Math.floor((SHEET_H + BIL_MM) / (b.h + BIL_MM)));
    var perSheet = perRow * perCol, sheets = Math.ceil(merki.length / perSheet);
    var H = SHEET_H * sheets + (sheets - 1) * SHEET_GAP;
    var out = { vara: v.vara, snid: v.vara + '-plata@3', w: SHEET_B, h: H, breidd: SHEET_B, haed: H, sheets: sheets, perSheet: perSheet, hlutir: [], lysing: [['Fjöldi', merki.length + ' skilti á ' + sheets + ' plötu(m), ' + perSheet + ' á plötu']], gogn: { vara: v.vara, staerd: v.staerd, litur: v.litur, snid: v.snid, gata: v.gata, merki: merki.slice() } };
    merki.forEach(function (m, i) {
      var sheet = Math.floor(i / perSheet), idx = i % perSheet, row = Math.floor(idx / perRow), col = idx % perRow;
      var dx = col * (b.b + BIL_MM), dy = sheet * (SHEET_H + SHEET_GAP) + row * (b.h + BIL_MM);
      var vi = uppsetning(valRaw); vi.ibud = /^\d/.test(String(m)) ? 'Íbúð ' + m : String(m);
      var sk = skipulag(vi);
      hlutirAf(vi, sk, dx, dy).forEach(function (hl) { hl.hopur = 'merki-' + (i + 1); out.hlutir.push(hl); });
      out.hlutir.push({ gerd: 'texti', lag: 'UTLINA', texti: String(m), letur: LETUR[LETUR_SKILTI].en, mm: 3, x: r3(dx + b.b / 2), y: r3(dy + b.h + 3.5), jofnun: 'midja' });
    });
    for (var s = 0; s < sheets; s++) out.hlutir.push({ gerd: 'rett', lag: 'UTLINA', x: 0, y: s * (SHEET_H + SHEET_GAP), w: SHEET_B, h: SHEET_H });
    return out;
  }

  var API = {
    STAERDIR: STAERDIR, STAERDIR_VARA: STAERDIR_VARA, LITIR: LITIR, FESTING: FESTING, BIL_SNID: BIL_SNID, SAM_SNID: SAM_SNID, FENUR_FLOKKAR: FENUR_FLOKKAR,
    FENUR_PAKKI: FENUR_PAKKI, FENUR_LITIR: FENUR_LITIR, fenurEfni: fenurEfni,
    SJALFGEFID: SJALFGEFID, LETUR: LETUR, LETUR_SKILTI: LETUR_SKILTI, LETUR_TUNNUR: LETUR_TUNNUR, GAT_R: GAT_R, GAT_INN: GAT_INN, HORN_R: HORN_R,
    uppsetning: uppsetning, leyfdarStaerdir: leyfdarStaerdir, teikna: teikna, stada: stada, smfram: smfram, hreidra: hreidra, efni: efni, litur: litur,
    setTakn: setTakn, setFenur: setFenur, setLeturMal: setLeturMal, TAPPAR: TAPPAR, HETTA_R: HETTA_R, TAPPI_GAT_R: TAPPI_GAT_R, tapparVirkt: function () { return TAPPAR_VIRKT; }, setTapparVirkt: function (b) { TAPPAR_VIRKT = !!b; },
    finnaHaed: finnaHaed, skipulag: skipulag, textBreidd: textBreidd, linuBox: linuBox, FENUR_HEITI: FENUR_HEITI, UTGAFA: '3.6.2'
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  else root.SMHus = API;
})(this);
